import React, { useEffect, useState, useCallback, useRef, useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { secureGet, secureDelete, secureGetPdf } from "../../config/api";
import { useHistory } from "react-router-dom";
import { PrimaryButton } from "../../components/PrimaryButton";
import { formatDateTime } from "../../utils/format";
import OptionsMenu from "../../components/OptionsMenu";
import Table from "../../components/Table";
import Filter from "../../components/Filter";
import DeletePopup from "../../components/DeletePopup";
import PageScroller from "../../components/PageScroller";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from '../../components/SnackbarNotification';
import UserContext from "../../Shared/UserContext";

import AddIcon from "../../icons/plusB.svg";
import EyeIcon from "../../icons/eye.svg";
import DeleteIcon from "../../icons/trashR.svg"
import DownloadIcon from "../../icons/downloadB.svg";

const CreditNotes = () => {
  const [, setCreditNotesData] = useState([]);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    clientId: null,
    commercialId: null,
    dateRange: {},
    status: null,
    needle: "",
  });
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const history = useHistory();
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const topRef = useRef(null);
  const location = useLocation();
  const { state: { role } } = useContext(UserContext);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isCommercial = role === "ROLE_COMMERCIAL";

  useEffect(() => {
    if (location.state?.showSnackbar) {
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const STATUS_COLORS = {
    VALIDATED: "#06D6A0",
    UNDER_APPROVAL: "#FEA26F",
  };

  const getStatusLabel = (status) => t(`br_status.${status}`);

  const columns = [
    { label: t(`columns.creditNoteNumber`), key: "creditNoteNumber" },
    {
      label: t(`columns.createdAt`), key: "createdAt",
      render: (date) => formatDateTime(date)
    },
    { label: "Client", key: "client", render: (client) => client?.name || "N/A" },
    { label: t(`columns.commercial`), key: "user", render: (user) => `${user.firstName} ${user.lastName}` },
    { label: "Total HT", key: "priceHT", },
    { label: "Total TTC", key: "priceTTC", },
    {
      key: "actions",
      render: (_, rowData) =>
        <div onClick={(e) => e.stopPropagation()}>
          <OptionsMenu options={getOptions(rowData)} />
        </div>
    },
  ];

  const getOptions = (item) => {
    const options = [
      { label: t(`actions.details`), icon: EyeIcon, action: () => handleDetails(item) },
      { label: t(`actions.downloadBA`), icon: DownloadIcon, action: () => handleDownload(item) },
      //{ label: t(`actions.delete`), color: '#EF466F', icon: DeleteIcon, action: () => handleDelete(item) }
    ];

    return options;
  };

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    {
      name: "dateRange",
      type: "daterange",
      get: true,
    },
    {
      name: "clientId",
      label: "Client",
      type: "dropdown",
      options: [...clients.map((client) => ({ value: client.id, reference: client.code, name: client.name }))],
    },
    {
      name: "vehicleId",
      label: "Véhicule",
      type: "dropdown",
      options: vehicles.map((v) => ({
        value: v.id,
        label: `${v.type || ''} - ${v.brandMake || ''} (${v.immatriculationNumber || ''})`,
        reference: `${v.immatriculationNumber} - ${v.color || ''}`,
        name: `${t(`filters.vehicles.${v.type}`)} - ${v.brand?.name || ''}`.trim(),
      })),
    }
  ];

  // dropdowns
  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    const fetchDropdowns = async () => {
      try {
        const clientsRes = await secureGet("/clients", showSnackbar, { signal });
        setClients(clientsRes.data || []);

        if (filters?.clientId && filters?.clientId !== null) {
          const vehiclesRes = await secureGet(`/vehicles/client/${filters?.clientId}`, showSnackbar, { signal });
          console.log("Vehicles fetched:", vehiclesRes.data);
          setVehicles(vehiclesRes.data || []);
        }
      } catch (e) {
          if (e.name !== "AbortError") console.error(e);
        }
      };

      fetchDropdowns();
      return () => abortController.abort();
    }, [filters?.clientId]);

  const fetchPaginatedCreditNotes = useCallback(
    async (page = 0, size = pageSize, activeFilters = filters) => {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const isCommercial = role === "ROLE_COMMERCIAL";

      try {
        const params = new URLSearchParams();
        params.set("page", page.toString());
        params.set("size", size.toString());

        if (activeFilters.needle) {
          params.set("needle", activeFilters.needle);
        }

        if (activeFilters.clientId !== null) {
          params.set("clientId", activeFilters.clientId.toString());
        }

        const commercialId = isCommercial
          ? user?.id
          : activeFilters.commercialId || null;

        if (commercialId && !isCommercial) {
          params.set("commercialId", commercialId.toString());
        }

        if (activeFilters.status) {
          params.set("status", activeFilters.status);
        }

        if (activeFilters.startDate) {
          params.set("startDate", activeFilters.startDate);
        }

        if (activeFilters.endDate) {
          params.set("endDate", activeFilters.endDate);
        }

        params.set("sort", "createdAt,desc");

        const endpoint = isCommercial
          ? `/creditNotes/mine?${params.toString()}`
          : `/creditNotes?${params.toString()}`;

        const response = await secureGet(endpoint, showSnackbar, { signal });
        console.log('Response Data:', response.data);

        const { content, totalPages } = response.data;
        setCreditNotesData(content);
        setCurrentPageData(content);
        setTotalPages(totalPages);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching credit notes:", error);
        }
      } finally {
        setLoading(false);
      }
    },
    [filters, pageSize, signal]
  );

  useEffect(() => {
    if (location.state?.filters || location.state?.page || location.state?.size) {
      const restoredFilters = location.state.filters || {};
      const restoredPage = location.state.page || 0;
      const restoredSize = location.state.size || 30;

      setFilters(restoredFilters);
      setCurrentPage(restoredPage);
      setPageSize(restoredSize);

      fetchPaginatedCreditNotes(restoredPage, restoredSize, restoredFilters);
    } else {
      fetchPaginatedCreditNotes(0, pageSize, filters);
    }
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaginatedCreditNotes(page, pageSize, filters);
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    fetchPaginatedCreditNotes(0, size, filters);
  };

  const handleFilterChange = (filterName, filterValue) => {
    const updatedFilters = { ...filters };

    if (filterName === "dateRange") {
      const { startDate, endDate } = filterValue || {};
      updatedFilters.startDate = startDate || "";
      updatedFilters.endDate = endDate || "";
    } else if (filterName === "status") {
      updatedFilters.status = filterValue;
    } else {
      updatedFilters[filterName] = filterValue;
    }

    setFilters(updatedFilters);
    setCurrentPage(0);
    fetchPaginatedCreditNotes(0, pageSize, updatedFilters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      clientId: null,
      commercialId: null,
      dateRange: {},
      status: null,
      needle: "",
    };
    setFilters(defaultFilters);
    setCurrentPage(0);
    fetchPaginatedCreditNotes(0, pageSize, defaultFilters);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      await secureDelete(`/creditNotes/delete/${itemToDelete.id}`, showSnackbar);
      setDeletePopupVisible(false);
      setItemToDelete(null);
      fetchPaginatedCreditNotes(currentPage, pageSize);
      showSnackbar(t(`confirmation.creditNoteDelete`), 'success');
    } catch (error) {
      console.error("Error deleting credit note:", error);
      setDeletePopupVisible(false);
    }
  };

  const handleDetails = (item) => {
    history.push({
      pathname: `/credit-notes/details/${item.id}`,
      state: {
        creditNote: item,
        filters,
        page: currentPage,
        size: pageSize,
      },
    });
  };

  const handleDownload = async (item) => {
    try {
      const response = await secureGetPdf(`/creditNotes/ba/${item.id}`, showSnackbar);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `credit_note_${item.creditNoteNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(t('confirmation.downloadSuccess'), 'success');
    } catch (error) {
      console.error("Failed to download credit note:", error);
    }
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>Drivnest - {t("titles.br")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] ">
        <h1 className="text-[40px] font-semibold">{t(`titles.br`)}</h1>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={(filterName, filterValue) => handleFilterChange(filterName, filterValue)}
        resetFilters={resetFilters}
        filterValues={filters}
      />
      <div style={{ position: "relative", minHeight: "200px" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1
            }}>
            <LoadingLogo />
          </div>
        )}
        <Table
          columns={columns}
          data={currentPageData}
          onRowClick={(row) => handleDetails(row)}
        />
      </div>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup open={isDeletePopupVisible} onCancel={() => setDeletePopupVisible(false)} onConfirm={handleDeleteConfirm} />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default CreditNotes;